// Canvas-movement handlers — per-frame updates that translate / rotate /
// scale the `world` group.
//
//   - handleCanvasDrag: index-trigger drag of the grab bar. The bar follows
//     the laser endpoint at the captured press-time distance, dragging the
//     whole canvas along with it.
//
//   - handleNavigation: dual-palm-grip gesture. Hand midpoint translates;
//     hand twist rotates around the grid's own center; hand spread / pinch
//     scales (anchored at the same center).
//
// Both need the `world` and `gridGroup` groups injected at init time
// since main.ts owns those.

import * as THREE from "three";
import {
  controllerEffectiveWorldPos,
  controllerStates,
  controllers,
  laserLocalDir,
} from "../../input/controllers";
import { interaction, setInteraction } from "../state";

let _world: THREE.Group | null = null;
let _gridGroup: THREE.Group | null = null;
let _getCameraWorldPos: ((out: THREE.Vector3) => void) | null = null;
// The grab bar's mesh, injected by the host (it lives in the UI layer, which
// sits above this one — importing it directly would invert the layering).
let _grabBarMesh: THREE.Object3D | null = null;

// The dual-palm-grip nav gesture (handleNavigation) normally drives the model
// canvas (`_world`), pivoting rotation/scale on the voxel grid's center and
// clamping scale to the model's range. The AR layout tabletop reuses the exact
// same gesture on a DIFFERENT target (floorScene.root) — swapped in via
// setNavContext and restored via resetNavContext. Only the per-frame transform
// target, pivot, and scale bounds change; the gesture math is shared.
let _navTarget: THREE.Object3D | null = null;
let _navPivot: ((out: THREE.Vector3) => void) | null = null;
let _navScaleMin = 0.1;
let _navScaleMax = 20;

// Don't let the user drag the grab bar (and with it, the canvas) any
// closer than this to the camera. Below this distance the bar's apparent-
// size compensation in updateGrabBar generates huge grabBarMesh.position
// values (worldOffset / tiny desiredCumulative), and the yaw recalc from
// the user's grid-local position swings wildly with small head motion —
// together that produces the visible "split in two and jitter" failure
// mode. 40cm keeps the math well-conditioned and is still close enough
// for fine inspection.
const MIN_CAM_TO_BAR = 0.4;

export function initNavigationHandlers(
  world: THREE.Group,
  gridGroup: THREE.Group,
  getCameraWorldPos: (out: THREE.Vector3) => void,
  grabBarMesh: THREE.Object3D,
): void {
  _world = world;
  _gridGroup = gridGroup;
  _getCameraWorldPos = getCameraWorldPos;
  _grabBarMesh = grabBarMesh;
  resetNavContext();
}

// Point the dual-grip gesture at an arbitrary object (used by the AR layout
// tabletop to manipulate floorScene.root). `pivot` writes the world-space point
// that rotation + scale anchor on; `scaleMin`/`scaleMax` bound the target's
// scale (the tabletop runs at a small scale, so its bounds differ from the
// model's). Both the target and the pivot's parent must live directly under the
// scene (identity world transform) so `target.position` is world-space — true
// for `world` and `floorScene.root` alike.
export function setNavContext(
  target: THREE.Object3D,
  pivot: (out: THREE.Vector3) => void,
  scaleMin: number,
  scaleMax: number,
): void {
  _navTarget = target;
  _navPivot = pivot;
  _navScaleMin = scaleMin;
  _navScaleMax = scaleMax;
}

// Restore the model-canvas defaults: move `_world`, pivot on the grid center.
export function resetNavContext(): void {
  _navTarget = _world;
  _navPivot = (out) => {
    out.set(0, 0, 0);
    _gridGroup?.localToWorld(out);
  };
  _navScaleMin = WORLD_SCALE_MIN;
  _navScaleMax = WORLD_SCALE_MAX;
}

// Index-trigger drag: each frame, project the controller's ray to the
// press-time distance and translate `world` so the bar's world position
// lands at that endpoint. world.rotation.y is left alone (rotating it on
// the press frame would yank the cube through space because of the non-
// zero gridGroup.position). grabBarGroup.rotation.y *does* update each
// frame via updateGrabBar (called just before this) so the bar tracks the
// user-facing side even during the drag — and since getWorldPosition
// picks that up, the cube's translation correctly compensates.
const _dragControllerPosTmp = new THREE.Vector3();
const _dragControllerDirTmp = new THREE.Vector3();
const _dragBarWorldTmp = new THREE.Vector3();
const _dragCamPosTmp = new THREE.Vector3();

export function handleCanvasDrag(): void {
  if (!_world || !_grabBarMesh) return;
  if (interaction.kind !== "canvas_drag") return;
  if (!controllerStates[interaction.controllerIndex].selecting) {
    setInteraction({ kind: "idle" });
    return;
  }
  const d = interaction;
  const c = controllers[d.controllerIndex];
  // Both origin and direction route through the precision-aim helpers
  // so dragging the canvas with precision engaged moves it slower for
  // fine positioning. suppressTilt: canvas-drag owns the squeeze
  // already and shouldn't pick up the 45° tilt.
  controllerEffectiveWorldPos(d.controllerIndex, _dragControllerPosTmp);
  laserLocalDir(d.controllerIndex, _dragControllerDirTmp, {
    suppressTilt: true,
  });
  _dragControllerDirTmp.transformDirection(c.matrixWorld).normalize();
  // The laser endpoint should land on the same spot the user originally
  // pressed (not the bar's center). hitOffset = pressPoint − barCenter
  // was captured at press time; subtracting it from the laser endpoint
  // gives the desired bar center this frame.
  let barTargetX =
    _dragControllerPosTmp.x +
    _dragControllerDirTmp.x * d.hitDist -
    d.hitOffset.x;
  let barTargetY =
    _dragControllerPosTmp.y +
    _dragControllerDirTmp.y * d.hitDist -
    d.hitOffset.y;
  let barTargetZ =
    _dragControllerPosTmp.z +
    _dragControllerDirTmp.z * d.hitDist -
    d.hitOffset.z;

  // Personal-space clamp: if the user is pulling the bar closer than
  // MIN_CAM_TO_BAR to their head, push the target back out along the
  // camera→target ray so distance == MIN_CAM_TO_BAR. The bar can still
  // be moved in any direction (above, beside, behind the user) — only
  // the "inside the head" zone is excluded. Skipped if the camera
  // getter isn't wired up or the target lands exactly at the camera.
  if (_getCameraWorldPos) {
    _getCameraWorldPos(_dragCamPosTmp);
    const dx = barTargetX - _dragCamPosTmp.x;
    const dy = barTargetY - _dragCamPosTmp.y;
    const dz = barTargetZ - _dragCamPosTmp.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > 1e-6 && distSq < MIN_CAM_TO_BAR * MIN_CAM_TO_BAR) {
      const k = MIN_CAM_TO_BAR / Math.sqrt(distSq);
      barTargetX = _dragCamPosTmp.x + dx * k;
      barTargetY = _dragCamPosTmp.y + dy * k;
      barTargetZ = _dragCamPosTmp.z + dz * k;
    }
  }

  // Bar's world position is currently `world.position + scale ·
  //   R_y(world.rotation.y) · (gridGroup.position +
  //   R_y(grabBarGroup.rotation.y) · grabBarMesh.position)`.
  // World.rotation.y and grabBarGroup.rotation.y are unchanged during the
  // drag (see updateGrabBar's early-return), so the offset between
  // world.position and bar's world position is constant. Compute the
  // current bar world via getWorldPosition, then add the delta to the
  // target.
  _grabBarMesh.getWorldPosition(_dragBarWorldTmp);
  _world.position.x += barTargetX - _dragBarWorldTmp.x;
  _world.position.y += barTargetY - _dragBarWorldTmp.y;
  _world.position.z += barTargetZ - _dragBarWorldTmp.z;
}

// Sensitivity multipliers on the dual-palm-grip nav gesture. Pan
// (hand-midpoint translation), rotate (yaw twist), and scale (hand
// spread) each get their own gain so they can be tuned independently.
// Scale uses an exponent rather than a multiplier so it stays gentle.
const NAV_PAN_GAIN = 2.88;
const NAV_ROTATE_GAIN = 1.6;
// < 1 makes scale less sensitive than 1:1: a 1.10× hand-spread ratio
// becomes 1.10^0.6 ≈ 1.058 canvas scale per frame. Compresses pinch /
// spread so the user has more travel for fine sizing.
const NAV_SCALE_GAIN = 0.6;
const WORLD_SCALE_MIN = 0.1;
const WORLD_SCALE_MAX = 20;

const navPosLTmp = new THREE.Vector3();
const navPosRTmp = new THREE.Vector3();
const navWorldCenterTmp = new THREE.Vector3();

export function handleNavigation(): void {
  const target = _navTarget;
  if (!target || !_navPivot) return;
  const bothSqueezing =
    controllerStates[0].squeezing && controllerStates[1].squeezing;

  if (!bothSqueezing) {
    if (interaction.kind === "navigate") setInteraction({ kind: "idle" });
    return;
  }
  if (interaction.kind !== "idle" && interaction.kind !== "navigate") return;

  controllers[0].getWorldPosition(navPosLTmp);
  controllers[1].getWorldPosition(navPosRTmp);
  const dist = navPosLTmp.distanceTo(navPosRTmp);
  // Midpoint between the two hands in world space — its motion frame-to-
  // frame drives the canvas translation.
  const midX = (navPosLTmp.x + navPosRTmp.x) / 2;
  const midY = (navPosLTmp.y + navPosRTmp.y) / 2;
  const midZ = (navPosLTmp.z + navPosRTmp.z) / 2;
  // Yaw of the line from left hand to right hand, projected onto the
  // horizontal plane. atan2(dx, dz) matches three.js' +Y rotation sense.
  const yaw = Math.atan2(
    navPosRTmp.x - navPosLTmp.x,
    navPosRTmp.z - navPosLTmp.z,
  );

  if (interaction.kind !== "navigate") {
    setInteraction({
      kind: "navigate",
      prevDist: dist,
      prevYaw: yaw,
      prevMidX: midX,
      prevMidY: midY,
      prevMidZ: midZ,
    });
    return;
  }

  const n = interaction;

  // Anchor for rotation and scale: the target's own world-space center (the
  // grid center for the model canvas, the layout center for the AR tabletop),
  // supplied by the active nav context. The target always pivots here, so
  // twisting the controllers spins it in place no matter where the user's hands
  // happen to be. Translation happens separately below: sliding both hands
  // together carries the target 1:1, hands-anywhere via the palm grips.
  _navPivot(navWorldCenterTmp);

  // Rotate: yaw change between the hands rotates the target around its own
  // center. Amplified by NAV_ROTATE_GAIN so a smaller hand twist covers
  // more angular motion of the canvas.
  let dYaw = (yaw - n.prevYaw) * NAV_ROTATE_GAIN;
  if (dYaw > Math.PI) dYaw -= 2 * Math.PI;
  else if (dYaw < -Math.PI) dYaw += 2 * Math.PI;
  if (dYaw !== 0) {
    const cosA = Math.cos(dYaw);
    const sinA = Math.sin(dYaw);
    const dx = target.position.x - navWorldCenterTmp.x;
    const dz = target.position.z - navWorldCenterTmp.z;
    // three.js RotY: x' = cos·x + sin·z, z' = -sin·x + cos·z. Matching
    // this exactly is what makes position and rotation cancel out so the
    // pivot point (the target's own center) stays fixed.
    target.position.x = navWorldCenterTmp.x + dx * cosA + dz * sinA;
    target.position.z = navWorldCenterTmp.z - dx * sinA + dz * cosA;
    target.rotation.y += dYaw;
  }

  // Translate: hand-midpoint motion carries the canvas in world,
  // scaled by NAV_PAN_GAIN. Pure delta-from-last-frame so the gesture
  // composes with rotate + scale without compounding — each contribution
  // is independent and the user perceives them simultaneously.
  target.position.x += (midX - n.prevMidX) * NAV_PAN_GAIN;
  target.position.y += (midY - n.prevMidY) * NAV_PAN_GAIN;
  target.position.z += (midZ - n.prevMidZ) * NAV_PAN_GAIN;

  // Scale: hands moving apart enlarges the target; hands together shrinks
  // it. Anchored at the target's world-space center so it stays in place.
  // Per-frame ratio raised to NAV_SCALE_GAIN — at 1.0 this collapses to the
  // original 1:1 ratio (pinch by N% scales canvas by N%), keeping scale feel
  // gentler than translation / rotation. Bounds come from the nav context.
  if (n.prevDist > 0.01 && dist > 0.01) {
    const rawRatio = dist / n.prevDist;
    const ratio = Math.pow(rawRatio, NAV_SCALE_GAIN);
    const next = THREE.MathUtils.clamp(
      target.scale.x * ratio,
      _navScaleMin,
      _navScaleMax,
    );
    const applied = next / target.scale.x;
    if (applied !== 1) {
      target.position.sub(navWorldCenterTmp);
      target.position.multiplyScalar(applied);
      target.position.add(navWorldCenterTmp);
      target.scale.multiplyScalar(applied);
    }
  }

  n.prevDist = dist;
  n.prevYaw = yaw;
  n.prevMidX = midX;
  n.prevMidY = midY;
  n.prevMidZ = midZ;
}
